/**
 * Bulk swap referee for a given date.
 * Admin only — replaces every occurrence of `fromId` with `toId` on all games
 * of the selected date. Detects conflicts (dispos, catégorie, collision, duo interdit).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X } from "lucide-react";

const REASON_LABELS = {
  category: "Catégorie non autorisée",
  blocked_category: "Catégorie bloquée pour le remplaçant",
  availability: "Non disponible ce jour",
  collision: "Conflit horaire avec ses autres parties",
  forbidden_pair: "Duo interdit avec l'autre arbitre",
};

export default function BulkSwapRefModal({ event, officials = [], initialFromId = "", initialDate = "", onClose, onApplied }) {
  const games = event.games || [];
  const dates = useMemo(() => {
    const s = new Set(games.filter((g) => !g.is_break && g.date).map((g) => g.date));
    return Array.from(s).sort();
  }, [games]);
  const [date, setDate] = useState(initialDate || dates[0] || "");
  const [fromId, setFromId] = useState(initialFromId);
  const [toId, setToId] = useState("");
  const [preview, setPreview] = useState(null); // {matched, updated, conflicts}
  const [busy, setBusy] = useState(false);

  const refs = officials.filter((o) => (o.roles || []).includes("referee"));
  const nameOf = (id) => {
    const o = refs.find((x) => x.id === id) || officials.find((x) => x.id === id);
    return o ? `${o.first_name} ${o.last_name}` : id;
  };

  // Arbitres présents sur les parties de la date sélectionnée
  const refsOnDate = useMemo(() => {
    const s = new Set();
    for (const g of games) {
      if (g.is_break || g.date !== date) continue;
      if (g.referee1_id) s.add(g.referee1_id);
      if (g.referee2_id) s.add(g.referee2_id);
    }
    return refs.filter((o) => s.has(o.id));
  }, [games, date, refs]);

  useEffect(() => { setPreview(null); }, [date, fromId, toId]);

  const runPreview = async (force = false) => {
    if (!date || !fromId || !toId) {
      toast.error("Sélectionne la date, l'arbitre absent et le remplaçant");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/events/${event.id}/bulk-swap-referee`,
        { date, from_official_id: fromId, to_official_id: toId, force },
      );
      if (!force && data.conflicts && data.conflicts.length) {
        setPreview(data);
        return;
      }
      toast.success(`${data.updated}/${data.matched} partie(s) remplacée(s)`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl border-2 border-black p-6 shadow-2xl" data-testid="bulk-swap-ref-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold uppercase tracking-wider">Remplacer un arbitre — journée</h2>
          <button onClick={onClose} className="p-1 hover:bg-black/5" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm mb-4 text-[var(--muted-2)]">
          Réattribue toutes les parties d&apos;un arbitre absent à un remplaçant, pour la journée sélectionnée.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <label className="text-xs">
            Date
            <select className="input-box w-full mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="swap-ref-date">
              {dates.map((d) => (<option key={d} value={d}>{d}</option>))}
            </select>
          </label>
          <label className="text-xs">
            Arbitre absent
            <select className="input-box w-full mt-1" value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="swap-ref-from">
              <option value="">—</option>
              {refsOnDate.map((o) => (<option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>))}
            </select>
          </label>
          <label className="text-xs">
            Remplaçant
            <select className="input-box w-full mt-1" value={toId} onChange={(e) => setToId(e.target.value)} data-testid="swap-ref-to">
              <option value="">—</option>
              {refs.filter((o) => o.id !== fromId).map((o) => (
                <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
              ))}
            </select>
          </label>
        </div>
        {preview && preview.conflicts && preview.conflicts.length > 0 && (
          <div className="border-2 border-black bg-white p-3 mb-4 text-xs" data-testid="swap-ref-conflicts">
            <div className="font-bold uppercase mb-1">
              {preview.conflicts.length} conflit(s) détecté(s) pour {nameOf(toId)}
            </div>
            <ul className="list-disc pl-4 mt-1 space-y-1">
              {preview.conflicts.slice(0, 8).map((c) => (
                <li key={c.game_id}>
                  <span className="font-mono">{c.start_time}</span> · Surface {c.surface} · {c.category} —
                  {" "}
                  {c.reasons.map((r) => REASON_LABELS[r] || r).join(" · ")}
                </li>
              ))}
              {preview.conflicts.length > 8 && <li>… et {preview.conflicts.length - 8} de plus</li>}
            </ul>
            <div className="mt-2">Forcer le remplacement malgré les conflits ?</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>Annuler</button>
          {preview && preview.conflicts && preview.conflicts.length > 0 ? (
            <button onClick={() => runPreview(true)} className="btn-primary" disabled={busy} data-testid="swap-ref-force">
              Forcer le remplacement
            </button>
          ) : (
            <button
              onClick={() => runPreview(false)}
              className="btn-primary"
              disabled={busy || !date || !fromId || !toId}
              data-testid="swap-ref-submit"
            >
              Remplacer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
